/**
 * Turn a util.fetchApi response from the Convene endpoints into a message we can show
 * the vet, or '' when the call succeeded.
 *
 * The Convene payment handler used to read
 * `response.data.error || response.data.title || response.data.message`, but on an
 * error the backend answers with the SALE in `data`
 * (`{ status, data: <sale>, message, error }` — see post-orders.js). `data.title` is
 * therefore `sale.title`, so the red error box rendered the owner and animal name
 * ("Ola Nordmann - Luna (5 yr)") while the real cause sat untouched in
 * `response.error` — the clinic had no way to see what Convene actually complained
 * about (T-01609). Always route Convene responses through this helper.
 *
 * Convene's own error bodies are ASP.NET shaped and PascalCased:
 * `{ Type: 'CorruptOrderException', Message: '...', AdditionalData: null }`.
 */

// The raw envelopes preact-util produces — an HTML dump or a JS stack is noise to a vet.
const OPAQUE_MESSAGE_PATTERN = /^ERROR (API did not return JSON|fetching API):/;

function stringifyError(error) {
    if (typeof error === 'string') {
        return error.trim();
    }
    // Convene uses `Message`/`Type`; our own routes use `error`/`message`.
    const candidates = [error.Message, error.message, error.error, error.title, error.Type];
    const text = candidates.find(c => typeof c === 'string' && c.trim());
    if (text) {
        return text.trim();
    }
    try {
        return JSON.stringify(error);
    } catch (err) {
        return '';
    }
}

export function conveneErrorMessage(response) {
    if (!response) {
        return 'Fikk ikke svar fra serveren. Sjekk nettforbindelsen og prøv igjen.';
    }

    const { error, status, message } = response;

    if (error) {
        const text = stringifyError(error);
        if (text) {
            return text;
        }
    }

    if (status >= 400) {
        const detail = `${message || ''}`.trim();
        if (!detail || OPAQUE_MESSAGE_PATTERN.test(detail)) {
            return `Uventet feil fra Convene (${status}). Prøv igjen, og kontakt support hvis det gjentar seg.`;
        }
        return detail;
    }

    return '';
}

export default conveneErrorMessage;
